import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header';
import SimpleAuthGuard from './components/SimpleAuthGuard';
import GlobalPaymentManager from './components/GlobalPaymentManager';
import PageLoader from './components/PageLoader';
import ErrorBoundary from './components/ErrorBoundary';
import Skeleton from './components/Skeleton';
import NetworkStatusBanner from './components/NetworkStatusBanner';
import { UserProvider, useUser } from './store/UserContext';
import { ThemeProvider } from './store/ThemeContext.jsx';
import Sidebar from './components/Sidebar';
import { useNavLayout } from './hooks/useNavLayout';
import { FreeTrialProvider } from './store/FreeTrialContext';
import { APP_NAME } from './config/brand';
import { t } from './i18n';

import WelcomePage from './pages/WelcomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AlipayCallbackPage from './pages/auth/AlipayCallbackPage';
import TenantOnboardingPage from './pages/onboarding/TenantOnboardingPage';
import NotFoundPage from './pages/NotFoundPage';
import OnboardingGate from './components/OnboardingGate';

import { getLegacyRedirectRoutes } from './routes/legacyRedirects';
import { getProductionRoutes } from './routes/productionRoutes';
import { getCoreRoutes } from './routes/coreRoutes';
import { getMyPlantProtectionRoutes } from './routes/myPlantProtectionRoutes';
import { getSystemRoutes } from './routes/systemRoutes';
import { getMallAndProfileRoutes } from './routes/mallAndProfileRoutes';

/** 路由懒加载占位：复用现有 Skeleton，不新增 UI 组件 */
function RouteLazyFallback() {
  return <Skeleton type="default" rows={4} />;
}

function AppContent() {
  const { user, loading, isInitialized, logout } = useUser();
  const { isHorizontal } = useNavLayout();
  const isAuthenticated = !!user;
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  /** 智慧农场驾驶舱全屏：隐藏侧栏与顶栏 */
  const hideSidebar = location.pathname.startsWith('/smart-farm-cockpit');

  useEffect(() => {
    document.title = hideSidebar ? t('nav_smart_farm_cockpit') : APP_NAME;
  }, [hideSidebar, location.pathname]);

  if (loading || !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const toggleMobileSidebar = () => {
    setMobileOpen((prev) => !prev);
  };

  const renderGuardedRoutes = (authenticated) => (
    <>
      {getLegacyRedirectRoutes()}
      {getProductionRoutes(authenticated)}
      {getCoreRoutes(authenticated)}
      {getMyPlantProtectionRoutes()}
      {getMallAndProfileRoutes(authenticated)}
      {getSystemRoutes(authenticated)}
    </>
  );

  /** 已登录：首页进欢迎页；未登录：禁止落欢迎页（后端未启动时也不应先进壳层再点进登录） */
  const guestEntryRoutes = (
    <>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/welcome" element={<Navigate to="/login" replace />} />
      <Route path="/auth/alipay/callback" element={<AlipayCallbackPage />} />
    </>
  );

  const authedEntryRoutes = (
    <>
      <Route path="/" element={<Navigate to="/welcome" replace />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/auth/alipay/callback" element={<AlipayCallbackPage />} />
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageLoader>
        <NetworkStatusBanner />
        {isAuthenticated ? (
          <div className="flex flex-col h-screen">
            {!hideSidebar && (
              <Header
                onLogout={handleLogout}
                onToggleMobileSidebar={toggleMobileSidebar}
                showMobileSidebarToggle
                mobileOpen={mobileOpen}
                setMobileOpen={setMobileOpen}
              />
            )}

            <div className="flex flex-1 overflow-hidden">
              {!hideSidebar && !isHorizontal && <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />}
              {!hideSidebar && !isHorizontal && mobileOpen && (
                <div
                  className="fixed inset-0 z-30 bg-black/30 md:hidden"
                  onClick={() => setMobileOpen(false)}
                  aria-hidden="true"
                />
              )}
              <main
                className={
                  hideSidebar
                    ? 'flex min-h-0 flex-1 flex-col overflow-hidden bg-[#020617] py-0'
                    : 'flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900 py-2 min-h-0'
                }
              >
                <div className={hideSidebar ? 'flex min-h-0 min-w-0 flex-1 flex-col' : 'contents'}>
                  <GlobalPaymentManager>
                    <SimpleAuthGuard>
                      <OnboardingGate>
                      {/* 懒加载失败走 ErrorBoundary；加载中走 Skeleton；resetPath 切页清错误态 */}
                      <ErrorBoundary resetPath={location.pathname}>
                        <Suspense fallback={<RouteLazyFallback />}>
                          <Routes>
                            {authedEntryRoutes}
                            <Route path="/onboarding/tenant" element={<TenantOnboardingPage />} />
                            <Route path="/login" element={<Navigate to="/welcome" replace />} />
                            <Route path="/register" element={<Navigate to="/welcome" replace />} />
                            <Route path="/reset-password" element={<Navigate to="/welcome" replace />} />
                            {renderGuardedRoutes(isAuthenticated)}
                            <Route path="*" element={<NotFoundPage />} />
                          </Routes>
                        </Suspense>
                      </ErrorBoundary>
                      </OnboardingGate>
                    </SimpleAuthGuard>
                  </GlobalPaymentManager>
                </div>
              </main>
            </div>
          </div>
        ) : (
          <ErrorBoundary>
            <Suspense fallback={<RouteLazyFallback />}>
              <Routes>
                {guestEntryRoutes}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                {renderGuardedRoutes(false)}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        )}
      </PageLoader>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <UserProvider>
        <FreeTrialProvider>
          <GlobalPaymentManager>
            <AppContent />
          </GlobalPaymentManager>
        </FreeTrialProvider>
      </UserProvider>
    </ThemeProvider>
  );
}

export default App;
